import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Designer } from "@/components/designer/Designer";
import { designPatch } from "@/components/designer/DesignButton";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { newDesign } from "@/lib/design";

const inp = "h-10 w-full rounded-lg border border-slate-800 bg-white px-3 text-sm text-slate-100 outline-none focus:border-amber-500/70";

function AddToQuote({ pending, onClose }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [target, setTarget] = useState("new");
  const [customer, setCustomer] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/quotes").then((r) => setQuotes((r.data.items || r.data).slice(0, 50))).catch(() => {}); }, []);
  const add = async () => {
    setBusy(true);
    try {
      const types = (await api.get("/catalog").catch(() => ({ data: {} }))).data.types || [];
      const line = { id: Math.random().toString(36).slice(2, 10), qty: 1, ...designPatch(pending.d, pending.p, user?.role === "owner", types) };
      let id = target;
      if (target === "new") {
        if (!customer.trim()) { setBusy(false); return toast.error("Enter a customer name"); }
        id = (await api.post("/quotes", { client_name: customer.trim(), items: [line] })).data.id;
      } else {
        const q = (await api.get(`/quotes/${target}`)).data;
        await api.put(`/quotes/${target}`, { ...q, items: [...q.items, line] });
      }
      toast.success("Design added to the quote");
      nav(`/quotes/${id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-to-quote-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add to quote</DialogTitle><DialogDescription>{pending.p.summary}</DialogDescription></DialogHeader>
        <select data-testid="add-to-quote-select" value={target} onChange={(e) => setTarget(e.target.value)} className={inp}>
          <option value="new">New quote…</option>
          {quotes.map((q) => <option key={q.id} value={q.id}>{q.number || q.id.slice(-6)} · {q.client_name}</option>)}
        </select>
        {target === "new" && <input data-testid="add-to-quote-customer-input" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name" className={inp} />}
        <button data-testid="add-to-quote-confirm" disabled={busy} onClick={add} className="h-10 rounded-full bg-amber-500 px-5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50">Add to quote</button>
      </DialogContent>
    </Dialog>
  );
}

export default function DesignPage() {
  const [pending, setPending] = useState(null);
  const [kind, setKind] = useState(null);
  return (
    <div className="space-y-8" data-testid="design-page">
      <div className="rise">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-500">Designer</p>
        <h1 className="font-display text-4xl font-semibold text-slate-50 sm:text-5xl">New design</h1>
        <p className="mt-3 text-sm text-slate-400 md:text-base">Build a window, door or assembly: sidelites, transoms, stacked units, grids per section. Then add it to a quote.</p>
      </div>
      {!kind ? (
        <div className="flex gap-3">
          <button data-testid="new-design-window" onClick={() => setKind("window")} className="rounded-2xl border border-slate-800 px-8 py-6 text-left transition-colors hover:border-amber-500"><p className="font-display text-2xl text-slate-50">Window</p><p className="text-xs text-slate-500">72 × 60 in, one fixed section</p></button>
          <button data-testid="new-design-door" onClick={() => setKind("door")} className="rounded-2xl border border-slate-800 px-8 py-6 text-left transition-colors hover:border-amber-500"><p className="font-display text-2xl text-slate-50">Door</p><p className="text-xs text-slate-500">36 × 80 in, one door leaf</p></button>
        </div>
      ) : (
        <Designer initial={kind === "door" ? newDesign("door", 36, 80) : newDesign("window", 72, 60)} saveLabel="Add to quote…" onSave={(d, p) => setPending({ d, p })} />
      )}
      {pending && <AddToQuote pending={pending} onClose={() => setPending(null)} />}
    </div>
  );
}
